import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AGENT_FUNNEL_DATA_SOURCE_CHOICES, AGENT_FUNNEL_PERIOD_PRESETS, AgentFunnelComparePeriod, AgentFunnelDataSource, AgentFunnelYMetric, fetchFunnelHeatmap, } from '@/entities/funnel-heatmap';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { cn } from '@/shared/lib/cn';
import { Alert, AlertDescription, AlertTitle, Button, Select, Sheet, Skeleton, } from '@/shared/ui';
import { RotateCcwIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { agentRowKey, cellSelectionFromPoint, } from '../lib/agent-funnel-list-rows';
import { cellMoversFromTransitions, flattenMoverGroups, stageMoversFromTransitions, } from '../lib/cell-movers-from-transitions';
import { dealSourceLabel } from '../lib/deal-source-labels';
import { formatDelta, profileCompletenessGradationLabel, smartisCapturedYAxisLabel, stageLabel, tgBotAvailabilityYAxisLabel, transitionCellLabel, } from '../lib/heatmap-labels';
import { useFunnelFilters } from '../lib/use-funnel-filters';
import { FunnelAgentSearch, funnelSearchLocationLabel } from './funnel-agent-search';
import { FunnelAgentsPanel } from './funnel-agents-panel';
import { FunnelChart } from './funnel-chart';
import { FunnelMultiSelect } from './funnel-multi-select';
import { FunnelTransitionsTable, transitionKey, } from './funnel-transitions-table';
const Y_METRIC_LABELS = {
    [AgentFunnelYMetric.OBJECT_VISITS]: 'Посещаемость объектов',
    [AgentFunnelYMetric.PROFILE_COMPLETENESS]: 'Заполненность анкеты',
    [AgentFunnelYMetric.SOCIAL_COMPLETENESS]: 'Заполненность соцсетей',
    [AgentFunnelYMetric.SMARTIS_CAPTURED]: 'Захваченность в Smartis',
    [AgentFunnelYMetric.TG_BOT_AVAILABILITY]: 'Доступность в ТГБоте',
    [AgentFunnelYMetric.UNITY_SOURCES]: 'Источники ЮНИТИ',
};
const COMPARE_LABELS = {
    [AgentFunnelComparePeriod.NONE]: 'Не сравнивать',
    [AgentFunnelComparePeriod.WEEK]: 'Неделя',
    [AgentFunnelComparePeriod.MONTH]: 'Месяц',
};
const DATA_SOURCE_LABELS = {
    [AgentFunnelDataSource.AGENTS]: 'ЮНИТИ',
    [AgentFunnelDataSource.CRM]: 'CRM',
};
const FILTER_LEGEND_CLASS = 'mb-1 block text-[length:var(--unity-font-size-caption)] font-medium text-muted-foreground';
export function FunnelHeatmap() {
    const { scope } = useAgentScope();
    const { qualityMonths, quantityMonths, yMetric, comparePeriod, dataSourceSelection, projectId, agencyNames, activeAgentsOnly, dealSources, queryParams, reportQueryKey, isDefaultFilters, setQualityMonths, setQuantityMonths, setYMetric, setComparePeriod, setDataSourceSelection, setProjectId, setAgencyNames, setActiveAgentsOnly, setDealSources, selectedCell, focusAgent, setSelectedCell, setFocusAgent, selectAgentInCell, clearSelection, resetFilters, } = useFunnelFilters();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);
    const hasDataRef = useRef(false);
    const queryParamsRef = useRef(queryParams);
    queryParamsRef.current = queryParams;
    const load = useCallback(async (signal) => {
        const isFirst = !hasDataRef.current;
        if (isFirst)
            setLoading(true);
        else
            setFetching(true);
        setError(null);
        try {
            const next = await fetchFunnelHeatmap({
                ...queryParamsRef.current,
                scope,
            });
            if (signal?.aborted)
                return;
            setData(next);
            hasDataRef.current = true;
        }
        catch (err) {
            if (signal?.aborted)
                return;
            setError(funnelLoadErrorMessage(err));
        }
        finally {
            if (signal?.aborted)
                return;
            setLoading(false);
            setFetching(false);
        }
    }, [scope]);
    useEffect(() => {
        const controller = new AbortController();
        void load(controller.signal);
        return () => {
            controller.abort();
        };
        // reportQueryKey — only real report filters; cell/focusAgent must not reload
    }, [load, reportQueryKey]);
    const projectOptions = useMemo(() => [
        { value: 'all', label: 'Все проекты' },
        ...(data?.meta.availableProjects ?? []).map((project) => ({
            value: String(project.id),
            label: project.name,
        })),
    ], [data?.meta.availableProjects]);
    const agencyOptions = useMemo(() => {
        const fromReport = data?.meta.availableAgencies ?? [];
        const merged = [...fromReport];
        for (const name of agencyNames) {
            const key = name.toLocaleLowerCase('ru');
            if (!merged.some((item) => item.toLocaleLowerCase('ru') === key)) {
                merged.push(name);
            }
        }
        return merged;
    }, [data?.meta.availableAgencies, agencyNames]);
    const dealSourceOptions = useMemo(() => data?.meta.availableDealSources ?? [], [data?.meta.availableDealSources]);
    const effectiveDealSources = useMemo(() => {
        if (dealSources.length > 0)
            return dealSources;
        return data?.meta.selectedDealSources ?? [];
    }, [dealSources, data?.meta.selectedDealSources]);
    const selected = selectedCell;
    const selectedTransition = useMemo(() => {
        if (!data || selected?.kind !== 'transition')
            return null;
        return (data.transitions.find((item) => transitionKey(item) === selected.transitionKey) ??
            null);
    }, [data, selected]);
    const panelMoverGroups = useMemo(() => {
        if (!data || !selected)
            return null;
        if (selected.kind === 'cellDelta') {
            return cellMoversFromTransitions(data.transitions, {
                quantityStageOrder: selected.quantityStageOrder,
                yBucket: selected.yBucket,
            });
        }
        if (selected.kind === 'totalDelta') {
            return stageMoversFromTransitions(data.transitions, selected.quantityStageOrder);
        }
        return null;
    }, [data, selected]);
    const panelAgents = useMemo(() => {
        if (!data || !selected)
            return [];
        if (panelMoverGroups) {
            return flattenMoverGroups(panelMoverGroups);
        }
        if (selected.kind === 'transition' && selectedTransition) {
            return selectedTransition.agents;
        }
        if (selected.kind === 'total') {
            return data.points.filter((point) => point.quantityStageOrder === selected.quantityStageOrder);
        }
        if (selected.kind === 'cell') {
            return data.points.filter((point) => point.quantityStageOrder === selected.quantityStageOrder &&
                Math.min(point.yBucket, data.meta.yBucketMax) === selected.yBucket);
        }
        return [];
    }, [data, selected, selectedTransition, panelMoverGroups]);
    useEffect(() => {
        if (selected == null && focusAgent != null) {
            setFocusAgent(null);
            return;
        }
        if (!data || loading)
            return;
        if (focusAgent == null)
            return;
        if (!panelAgents.some((agent) => agentRowKey(agent) === focusAgent)) {
            setFocusAgent(null);
        }
    }, [data, loading, selected, focusAgent, panelAgents, setFocusAgent]);
    /** Axis flags from loaded report meta — never mix URL yMetric with stale axes. */
    const displayYMetric = data?.meta.yMetric ?? yMetric;
    const isProfileCompleteness = displayYMetric === AgentFunnelYMetric.PROFILE_COMPLETENESS;
    const isSocialCompleteness = displayYMetric === AgentFunnelYMetric.SOCIAL_COMPLETENESS;
    const isSmartisCaptured = displayYMetric === AgentFunnelYMetric.SMARTIS_CAPTURED;
    const isTgBotAvailability = displayYMetric === AgentFunnelYMetric.TG_BOT_AVAILABILITY;
    const isUnitySources = displayYMetric === AgentFunnelYMetric.UNITY_SOURCES;
    const isPercentGradationAxis = isProfileCompleteness || isSocialCompleteness || isUnitySources;
    const isNonVisitYAxis = isPercentGradationAxis || isSmartisCaptured || isTgBotAvailability;
    const filterIsUnitySources = yMetric === AgentFunnelYMetric.UNITY_SOURCES;
    /** Keep the last report on screen while a filter refetch runs. */
    const showHeatmapBody = Boolean(data) && !loading;
    const transitionLabelOptions = useMemo(() => ({
        qualityMonths,
        profileCompletenessGradationAxis: isPercentGradationAxis,
        smartisCapturedAxis: isSmartisCaptured,
        tgBotAvailabilityAxis: isTgBotAvailability,
    }), [qualityMonths, isPercentGradationAxis, isSmartisCaptured, isTgBotAvailability]);
    const panelTitle = useMemo(() => {
        if (!selected)
            return '';
        if (selected.kind === 'transition' && selectedTransition) {
            return `${selectedTransition.count} агентов · переход`;
        }
        if (selected.kind === 'cellDelta') {
            const cell = data?.cells.find((item) => item.xKey === String(selected.quantityStageOrder) &&
                item.yKey === String(selected.yBucket));
            const deltaLabel = formatDelta(cell?.delta ?? null);
            const yLabel = transitionCellLabel(selected.quantityStageOrder, selected.yBucket, qualityMonths, transitionLabelOptions);
            const moverCount = panelMoverGroups
                ? panelMoverGroups.in.length + panelMoverGroups.out.length
                : 0;
            return `Δ ${deltaLabel ?? '—'} · ${moverCount} агентов · ${yLabel}`;
        }
        if (selected.kind === 'totalDelta') {
            const total = data?.stageTotals.find((item) => item.quantityStageOrder === selected.quantityStageOrder);
            const deltaLabel = formatDelta(total?.delta ?? null);
            const moverCount = panelMoverGroups
                ? panelMoverGroups.in.length + panelMoverGroups.out.length
                : 0;
            return `Δ ${deltaLabel ?? '—'} · ${moverCount} агентов · ИТОГО · ${stageLabel(selected.quantityStageOrder)}`;
        }
        if (selected.kind === 'total') {
            return `${panelAgents.length} агентов · ИТОГО · ${stageLabel(selected.quantityStageOrder)}`;
        }
        if (selected.kind === 'cell') {
            let yLabel;
            if (isPercentGradationAxis) {
                const noun = isSocialCompleteness
                    ? 'соцсетей'
                    : isUnitySources
                        ? 'источники ЮНИТИ'
                        : 'анкеты';
                yLabel = `${profileCompletenessGradationLabel(selected.yBucket)} ${noun}`;
            }
            else if (isSmartisCaptured)
                yLabel = smartisCapturedYAxisLabel(selected.yBucket).toLowerCase();
            else if (isTgBotAvailability)
                yLabel = tgBotAvailabilityYAxisLabel(selected.yBucket).toLowerCase();
            else if (selected.yBucket === 0)
                yLabel = `ни разу за ${qualityMonths} мес.`;
            else
                yLabel = `${selected.yBucket} объект(ов)`;
            return `${panelAgents.length} агентов · ${stageLabel(selected.quantityStageOrder)} · ${yLabel}`;
        }
        return '';
    }, [
        selected,
        selectedTransition,
        panelAgents.length,
        qualityMonths,
        isSocialCompleteness,
        isUnitySources,
        isPercentGradationAxis,
        isSmartisCaptured,
        isTgBotAvailability,
        transitionLabelOptions,
        data?.cells,
        data?.stageTotals,
        panelMoverGroups,
    ]);
    const panelSubtitle = useMemo(() => {
        if (selected?.kind === 'cellDelta' || selected?.kind === 'totalDelta') {
            return 'Кто пришёл в эту клетку и кто ушёл за период сравнения. Если агента не было раньше, он тоже в списке «Пришли».';
        }
        if (isSocialCompleteness) {
            return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство и соцсети.';
        }
        if (isSmartisCaptured) {
            return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство и захват в Smartis.';
        }
        if (isTgBotAvailability) {
            return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство и Telegram-бот.';
        }
        if (isUnitySources) {
            return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство и доля источников.';
        }
        if (isProfileCompleteness) {
            return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство и анкета.';
        }
        return 'Нажмите на агента, чтобы увидеть объекты. Список можно скачать в Excel: ФИО, телефон, агентство.';
    }, [
        selected?.kind,
        isSocialCompleteness,
        isProfileCompleteness,
        isSmartisCaptured,
        isTgBotAvailability,
        isUnitySources,
    ]);
    const searchedAgent = useMemo(() => {
        if (!data || focusAgent == null)
            return null;
        return data.points.find((point) => agentRowKey(point) === focusAgent) ?? null;
    }, [data, focusAgent]);
    const searchLocationLabel = useMemo(() => {
        if (!data || !searchedAgent)
            return null;
        return funnelSearchLocationLabel(searchedAgent, data.meta.yBucketMax, qualityMonths, {
            profileCompletenessGradationAxis: isPercentGradationAxis,
            socialCompletenessAxis: isSocialCompleteness,
            smartisCapturedAxis: isSmartisCaptured,
            tgBotAvailabilityAxis: isTgBotAvailability,
        });
    }, [
        data,
        searchedAgent,
        qualityMonths,
        isPercentGradationAxis,
        isSocialCompleteness,
        isSmartisCaptured,
        isTgBotAvailability,
    ]);
    const handleSelect = (next) => {
        if (next &&
            selected &&
            next.kind === selected.kind &&
            (next.kind === 'cellDelta' || next.kind === 'totalDelta') &&
            'quantityStageOrder' in next &&
            'quantityStageOrder' in selected &&
            next.quantityStageOrder === selected.quantityStageOrder &&
            (next.kind !== 'cellDelta' ||
                (selected.kind === 'cellDelta' && next.yBucket === selected.yBucket))) {
            clearSelection();
            return;
        }
        setSelectedCell(next);
    };
    const handleTransitionSelect = (key) => {
        setSelectedCell({ kind: 'transition', transitionKey: key });
    };
    const handleSearchSelect = (point) => {
        if (!data)
            return;
        selectAgentInCell(cellSelectionFromPoint(point, data.meta.yBucketMax), agentRowKey(point));
    };
    const panelProps = {
        title: panelTitle,
        subtitle: panelSubtitle,
        agents: panelAgents,
        moverGroups: panelMoverGroups,
        moverLabelOptions: transitionLabelOptions,
        yMetric,
        selectedAgentKey: focusAgent,
        onSelectedAgentKeyChange: setFocusAgent,
        onClose: clearSelection,
    };
    const filtersBusy = loading || fetching;
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("section", { className: "flex flex-wrap items-end gap-x-3 gap-y-3", "aria-label": "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0432\u043E\u0440\u043E\u043D\u043A\u0438", children: [_jsx(FilterSelect, { label: "\u0412\u043E\u0432\u043B\u0435\u0447\u0451\u043D\u043D\u043E\u0441\u0442\u044C", value: String(qualityMonths), onValueChange: (value) => setQualityMonths(Number(value)), options: AGENT_FUNNEL_PERIOD_PRESETS.map((months) => ({
                            value: String(months),
                            label: `${months} мес.`,
                        })), disabled: filtersBusy, className: "w-32" }), _jsx(FilterSelect, { label: "\u0412\u043E\u0440\u043E\u043D\u043A\u0430", value: String(quantityMonths), onValueChange: (value) => setQuantityMonths(Number(value)), options: AGENT_FUNNEL_PERIOD_PRESETS.map((months) => ({
                            value: String(months),
                            label: `${months} мес.`,
                        })), disabled: filtersBusy, className: "w-32" }), _jsx(FilterSelect, { label: "\u0412\u0435\u0440\u0442\u0438\u043A\u0430\u043B\u044C\u043D\u0430\u044F \u043E\u0441\u044C", value: yMetric, onValueChange: (value) => setYMetric(value), options: Object.values(AgentFunnelYMetric).map((metric) => ({
                            value: metric,
                            label: Y_METRIC_LABELS[metric],
                        })), disabled: filtersBusy, className: "w-52" }), _jsx(FilterSelect, { label: "\u0421\u0440\u0430\u0432\u043D\u0438\u0442\u044C \u0441", value: comparePeriod, onValueChange: (value) => setComparePeriod(value), options: Object.values(AgentFunnelComparePeriod).map((period) => ({
                            value: period,
                            label: COMPARE_LABELS[period],
                        })), disabled: filtersBusy, className: "w-32" }), _jsx(FilterSelect, { label: "\u041F\u0440\u043E\u0435\u043A\u0442", value: projectId === 'all' ? 'all' : String(projectId), onValueChange: (value) => setProjectId(value === 'all' ? 'all' : Number(value)), options: projectOptions, disabled: filtersBusy, className: "w-44" }), _jsx(FunnelMultiSelect, { label: "\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A", options: AGENT_FUNNEL_DATA_SOURCE_CHOICES.map((source) => ({
                            value: source,
                            label: DATA_SOURCE_LABELS[source],
                        })), selected: dataSourceSelection, onChange: (next) => {
                            if (next.length === 0)
                                return;
                            setDataSourceSelection(next);
                        }, placeholder: "\u042E\u041D\u0418\u0422\u0418", allowEmpty: false, disabled: filtersBusy, className: "w-40" }), _jsx(FunnelMultiSelect, { label: "\u0410\u0433\u0435\u043D\u0442\u0441\u0442\u0432\u0430", options: agencyOptions.map((agency) => ({
                            value: agency,
                            label: agency,
                        })), selected: agencyNames, onChange: setAgencyNames, placeholder: "\u0412\u0441\u0435 \u0430\u0433\u0435\u043D\u0442\u0441\u0442\u0432\u0430", emptyText: "\u041F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0430\u0433\u0435\u043D\u0442\u0441\u0442\u0432", disabled: filtersBusy, className: "w-52" }), filterIsUnitySources ? (_jsx(FunnelMultiSelect, { label: "\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A\u0438 CRM", options: dealSourceOptions.map((source) => ({
                            value: source,
                            label: dealSourceLabel(source),
                        })), selected: effectiveDealSources, onChange: setDealSources, placeholder: "\u041F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E: \u042E\u041D\u0418\u0422\u0418", emptyText: "\u041F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0438\u0441\u0442\u043E\u0447\u043D\u0438\u043A\u043E\u0432", disabled: filtersBusy, className: "w-56" })) : null, _jsxs("div", { className: "w-40", children: [_jsx("span", { className: FILTER_LEGEND_CLASS, children: "\u0410\u043A\u0442\u0438\u0432\u043D\u043E\u0441\u0442\u044C" }), _jsxs("label", { className: cn('flex h-9 cursor-pointer items-center gap-2 text-sm text-foreground', filtersBusy && 'pointer-events-none opacity-50'), children: [_jsx("input", { type: "checkbox", className: "size-4 accent-[var(--unity-color-brand)]", checked: activeAgentsOnly, disabled: filtersBusy, onChange: (event) => setActiveAgentsOnly(event.target.checked) }), _jsx("span", { className: "truncate", title: "\u0410\u0433\u0435\u043D\u0442\u044B, \u0443 \u043A\u043E\u0442\u043E\u0440\u044B\u0445 \u0437\u0430 \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0435 30 \u0434\u043D\u0435\u0439 \u0431\u044B\u043B\u043E \u0445\u043E\u0442\u044F \u0431\u044B \u043E\u0434\u043D\u043E \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435", children: "\u0422\u043E\u043B\u044C\u043A\u043E \u0430\u043A\u0442\u0438\u0432\u043D\u044B\u0435" })] })] }), _jsx(FunnelAgentSearch, { className: "min-w-52 flex-1 basis-52", points: data?.points ?? [], selectedKey: searchedAgent ? focusAgent : null, locationLabel: searchLocationLabel, disabled: filtersBusy, isLoading: filtersBusy, onSelect: handleSearchSelect, onClear: clearSelection }), _jsxs(Button, { type: "button", variant: "ghost", size: "sm", className: "h-9 min-h-9", disabled: isDefaultFilters || filtersBusy, onClick: () => resetFilters(), children: [_jsx(RotateCcwIcon, { className: "size-3.5 shrink-0" }), "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440\u044B"] })] }), error ? (_jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3", children: [_jsxs(Alert, { variant: "destructive", className: "flex-1", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error })] }), _jsx(Button, { type: "button", variant: "outline", onClick: () => void load(), children: uiCopy.retry })] })) : null, loading && !data ? _jsx(FunnelMatrixSkeleton, {}) : null, showHeatmapBody && data ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: cn('min-w-0', fetching && 'pointer-events-none opacity-60'), "aria-busy": fetching || undefined, children: [_jsx(FunnelChart, { axes: data.axes, cells: data.cells, stageTotals: data.stageTotals, yAxisTitle: isProfileCompleteness
                                    ? 'Заполненность анкеты'
                                    : isSocialCompleteness
                                        ? 'Заполненность соцсетей'
                                        : isSmartisCaptured
                                            ? 'Захваченность в Smartis'
                                            : isTgBotAvailability
                                                ? 'Доступность в ТГБоте'
                                                : isUnitySources
                                                    ? 'Источники ЮНИТИ'
                                                    : 'Вовлечённость', selected: selected, onSelect: handleSelect }), _jsxs("p", { className: "mt-2 text-[length:var(--unity-font-size-caption)] text-muted-foreground", children: ["\u0410\u0433\u0435\u043D\u0442\u043E\u0432: ", data.meta.totalAgents, ". \u041E\u043A\u043D\u0430: \u0432\u043E\u0440\u043E\u043D\u043A\u0430", ' ', data.meta.quantityPeriodMonths, " \u043C\u0435\u0441.", isNonVisitYAxis
                                        ? ''
                                        : `, вовлечённость ${data.meta.qualityPeriodMonths} мес.`, comparePeriod !== AgentFunnelComparePeriod.NONE
                                        ? ` · сравнение с предыдущ${comparePeriod === AgentFunnelComparePeriod.WEEK ? 'ей неделей' : 'им месяцем'}`
                                        : '', activeAgentsOnly ? ' · только активные агенты (30 дней)' : '', isUnitySources && effectiveDealSources.length > 0
                                        ? ` · источники: ${effectiveDealSources.map(dealSourceLabel).join(', ')}`
                                        : ''] })] }), _jsx(Sheet, { open: selected !== null, onClose: clearSelection, title: panelTitle || 'Агенты', side: "right", children: _jsx(FunnelAgentsPanel, { ...panelProps, embedded: true }) }), comparePeriod !== AgentFunnelComparePeriod.NONE ? (_jsxs("section", { className: "flex flex-col gap-2", children: [_jsx("h3", { className: "text-sm font-semibold", children: "\u041A\u0430\u043A \u0438\u0437\u043C\u0435\u043D\u0438\u043B\u0441\u044F \u0441\u043E\u0441\u0442\u0430\u0432 \u043A\u043B\u0435\u0442\u043E\u043A" }), _jsx(FunnelTransitionsTable, { transitions: data.transitions, selectedKey: selected?.kind === 'transition' ? selected.transitionKey : null, onSelect: handleTransitionSelect })] })) : null] })) : null] }));
}
const SKELETON_COLS = 6;
const SKELETON_ROWS = 5;
function FunnelMatrixSkeleton() {
    return (_jsx("div", { className: "w-full min-w-0 rounded-[var(--unity-radius)] border border-border bg-card p-3 sm:p-4", "aria-busy": "true", children: _jsx("div", { className: "w-full min-w-0 overflow-x-auto", children: _jsxs("div", { className: "grid w-full min-w-[28rem] gap-x-3 gap-y-1", style: {
                    gridTemplateColumns: `64px repeat(${SKELETON_COLS}, minmax(0, 1fr))`,
                }, children: [_jsx("div", { "aria-hidden": true }), Array.from({ length: SKELETON_COLS }).map((_, colIndex) => (_jsx(Skeleton, { className: "mx-auto h-3 w-12" }, `h-${colIndex}`))), Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (_jsxs("div", { className: "contents", children: [_jsx(Skeleton, { className: "h-3 w-10 justify-self-end self-center" }), Array.from({ length: SKELETON_COLS }).map((_, colIndex) => (_jsx(Skeleton, { className: "h-[52px] w-full rounded-[var(--unity-radius)]" }, `c-${rowIndex}-${colIndex}`)))] }, `row-${rowIndex}`))), _jsx("div", { "aria-hidden": true }), Array.from({ length: SKELETON_COLS }).map((_, colIndex) => (_jsx(Skeleton, { className: "h-[52px] w-full" }, `t-${colIndex}`)))] }) }) }));
}
function FilterSelect({ label, value, onValueChange, options, disabled, className, }) {
    return (_jsxs("label", { className: cn('min-w-0', className), children: [_jsx("span", { className: FILTER_LEGEND_CLASS, children: label }), _jsx(Select, { className: "w-full", size: "compact", value: value, disabled: disabled, onValueChange: onValueChange, options: options, "aria-label": label })] }));
}
function funnelLoadErrorMessage(err) {
    if (!(err instanceof ApiError))
        return uiCopy.loadFailed;
    if (err.status === 502 &&
        /CRM Query API HTTP|CRM Query API недоступен/i.test(err.message)) {
        return uiCopy.crmDownHint;
    }
    return err.message || uiCopy.loadFailed;
}
